import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Enums from "../assets/JsonData/Enums";
import { sendPushNotification, resetNotificationState } from "../redux/slices/NotificationSlice";

const NotificationPage = () => {
  const dispatch = useDispatch();

  const { loadingPush, errorPush, successPush } = useSelector(state => state.notifications);
  const { loading_getUsersInfos, refreshUserPage, usersInfos } = useSelector((state) => ({ ...state.userInfos }));

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientOption, setRecipientOption] = useState("multiple");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  
  // Liste filtrée selon option choisie
  const filteredUsers = useMemo(() => {
    if (!usersInfos?.data) return [];
    if (recipientOption === "multiple") return usersInfos.data;

    let wantedRole = null;
    switch (recipientOption) {
      case "guides":
        wantedRole = 2;
        break;
      case "consultants":
        wantedRole = 1;
        break;
      case "all":
        wantedRole = null;
        break;
      default:
        wantedRole = null;
    }

    if (wantedRole === null) return usersInfos.data;

    return usersInfos.data.filter((u) => u.role === wantedRole);
  }, [usersInfos, recipientOption]);

  // Checkbox toggle
  const toggleCheckbox = (userId) => {
    setSelectedRecipients((old) =>
      old.includes(userId) ? old.filter((id) => id !== userId) : [...old, userId]
    );
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
    setTitle("");
    setContent("");
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  // Envoi global ou modal individuel
  const handleSendNotifications = () => {
    let userIdsToSend = [];

    if (showModal && selectedUser) {
      // Envoi modal individuel
      userIdsToSend = [selectedUser.userId];
    } else {
      // Envoi global
      if (recipientOption === "multiple") {
        userIdsToSend = selectedRecipients;
      } else {
        userIdsToSend = filteredUsers.map(u => u.userId);
      }
    }

    if (!title.trim() || !content.trim()) {
      alert("Le titre et le contenu sont obligatoires");
      return;
    }
    if (userIdsToSend.length === 0) {
      alert("Veuillez sélectionner au moins un destinataire");
      return;
    }

    dispatch(sendPushNotification({
      userIds: userIdsToSend,
      push: { title: title.trim(), body: content.trim() },
      redirection: 0,
      parentId: "",
      pushType: 0,
    }));

    if (showModal) closeModal();
    else setSelectedRecipients([]);
    setTitle("");
    setContent("");
  };


  return (
    <div>
      <h2 className="page-header">Notifications</h2>

      {/* Formulaire d'envoi global */}
      <div className="card">
        <div className="card__body">
          <h3>💬 Envoyer une notification</h3>

          <div className="form-group">
            <label>Destinataires</label>
            <select
              value={recipientOption}
              onChange={(e) => {
                setRecipientOption(e.target.value);
                setSelectedRecipients([]);
              }}
            >
              <option value="multiple">Un ou plusieurs utilisateurs</option>
              <option value="guides">Tous les guides</option>
              <option value="consultants">Tous les consultants</option>
              <option value="all">Tous les utilisateurs</option>
            </select>
          </div>

          {recipientOption === "multiple" && (
            <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #ccc", marginBottom: 15 }}>
              {filteredUsers.map((user) => (
                <label key={user.userId} style={{ display: "block", padding: "4px 8px" }}>
                  <input
                    type="checkbox"
                    checked={selectedRecipients.includes(user.userId)}
                    onChange={() => toggleCheckbox(user.userId)}
                  />{" "}
                  {user.firstName} {user.lastName} ({Enums.matchClass(user.role)})
                </label>
              ))}
            </div>
          )}

          {(recipientOption !== "multiple") && (
            <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #ccc", marginBottom: 15 }}>
              {filteredUsers.map((user) => (
                <div key={user.userId} style={{ padding: "4px 8px" }}>
                  {user.firstName} {user.lastName} ({Enums.matchClass(user.role)})
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label>Titre (max 50 caractères)</label>
            <input
              type="text"
              maxLength={50}
              placeholder="Titre de la notification"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Contenu (max 150 caractères)</label>
            <textarea
              maxLength={150}
              placeholder="Contenu de la notification"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          <button className="btn" onClick={handleSendNotifications} disabled={loadingPush}>
            {loadingPush ? "Envoi en cours..." : `📨 Envoyer à ${
              recipientOption === "multiple" ? selectedRecipients.length : filteredUsers.length
            } utilisateur(s)`}
          </button>

          {errorPush && <div style={{ color: "red", marginTop: 10 }}>{errorPush}</div>}
          {successPush && <div style={{ color: "green", marginTop: 10 }}>Notification envoyée avec succès !</div>}
        </div>
      </div>

      {/* Modal d'envoi individuel */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Envoyer une notif à {selectedUser?.firstName} {selectedUser?.lastName}</h3>
            <div className="form-group">
              <label>Titre</label>
              <input type="text" maxLength={100} placeholder="Titre de la notification" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Contenu</label>
              <textarea maxLength={300} placeholder="Contenu de la notification" value={content} onChange={(e) => setContent(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSendNotifications} disabled={loadingPush}>
                {loadingPush ? "Envoi en cours..." : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPage;
